import { isFunction } from 'lodash';
// services
import { getCountryFromLanguage, getShortLanguageCode } from 'modules/language-provider/locale';
// epics
import { initAnalyticsObservable$ } from './analytics-provider.epic';
// utils
import { getEnvDependentEndpoint } from '@sitegen/common/utils/ubi-services';
// constants
import {
    ANALYTICS_EVENTS,
    ANALYTICS_GLOBAL_NS,
    ANALYTICS_READY_EVENT,
    ANALYTICS_SCRIPT_URL,
    ANALYTICS_SDK,
    SITE_CATEGORY,
    ANALYTICS_ATTRIBUTES,
    DEFAULT_LOCATION
} from './analytics-provider.constants';
import { COUNTRIES } from '@sitegen/common/localization/locations';
import { WEBSITE_DEFAULT_ANALYTIC_NAME } from '@sitegen/common/constants/website';
// types
import {
    IAccountEventData,
    IAnalyticsConfigData,
    IAnalyticsData,
    IFlowEventData,
    IPageEventData,
    ITrackingData,
    ITrackingEvent
} from 'types/analytics';

class Analytics {
    public isAuthenticatedUser = false;
    private isEnabled = false;
    private hasUserAllowedTracking = false;
    private flowType: string = '';
    private flowStep: string = '';
    private hostDomain: string = '';
    public platformSelection: string = '';
    private analyticsEnvironment: string;
    private trackingEventsBuffer: ITrackingEvent[] = [];

    public initAnalytics = (config: IAnalyticsConfigData): void => {
        if (!config.isAnalyticsEnabled) {
            return;
        }

        this.isEnabled = config.isAnalyticsEnabled;
        this.analyticsEnvironment = config.envName;
        this.hostDomain = window.location.hostname || '';
        this.flowType = config.flowType ?? '';

        window.addEventListener('message', (event: MessageEvent) => {
            if (ANALYTICS_READY_EVENT === event.data) {
                this.hasUserAllowedTracking = true;
                this.flushTrackingEventsBuffer();
            }
        });

        if (!config.embedded) {
            this.assignInitialData(config);
            this.injectAnalyticsScript(config.envName);
        } else {
            this.hasUserAllowedTracking = window[ANALYTICS_SDK]?.config.analyticsAccepted ?? false;
        }

        return initAnalyticsObservable$.next(config.isAnalyticsEnabled);
    };

    public sendPageEvent = (data: IAnalyticsData) => {
        this.updateAnalyticalData(data);

        const websiteData: IAnalyticsData = window[ANALYTICS_GLOBAL_NS];

        if (!this.isEnabled || !websiteData) {
            return;
        }

        const pageEventData = {
            appId: websiteData.appId,
            brand: websiteData.brand,
            country: websiteData.country,
            countryCode: websiteData.countryCode,
            environment: websiteData.environment,
            game: websiteData.game,
            genomeId: websiteData.genomeId,
            language: websiteData.language,
            region: websiteData.region,
            pageName: websiteData.pageName,
            siteName: websiteData.siteName,
            siteType: websiteData.siteType,
            siteSection: WEBSITE_DEFAULT_ANALYTIC_NAME,
            siteSubSection: websiteData.siteSubSection,
            ubisoftId: websiteData.ubisoftId,
            ...data
        } as IPageEventData;

        return this.track(ANALYTICS_EVENTS.PAGE_EVENT, pageEventData);
    };

    public sendAccountEvent = (data: IAccountEventData) => {
        const websiteData: IAnalyticsData = window[ANALYTICS_GLOBAL_NS];

        if (!websiteData || !this.isEnabled) {
            return;
        }

        this.track(ANALYTICS_EVENTS.ACCOUNT_EVENT, {
            ...data,
            action: [
                websiteData.siteName,
                ANALYTICS_ATTRIBUTES.category,
                data.action
            ].join(' - '),
            ubisoftId: websiteData.ubisoftId,
        });
    };

    public sendFlowEvent = (data: Omit<IFlowEventData, 'flowType'>): void => {
        const websiteData: IAnalyticsData = window[ANALYTICS_GLOBAL_NS];

        if (data.platformSelection && this.platformSelection !== data.platformSelection) {
            this.platformSelection = data.platformSelection;
        }

        if (data.flowStep && this.flowStep !== data.flowStep) {
            this.flowStep = data.flowStep;
        }

        const flowType = this.flowType ?? websiteData?.flowType;
        const flowEvent: IFlowEventData = {
            ...data,
            flowType,
            locationDetail: flowType,
            location: data.location || DEFAULT_LOCATION,
            flowStep: data?.flowStep ?? this.flowStep,
            platformSelection: this.platformSelection || websiteData?.platformSelection || '',
            hostDomain: this.hostDomain,
        };

        this.track(ANALYTICS_EVENTS.FLOW_EVENT, flowEvent);
    };

    public setPlatformSelection = (platform: string = '') => {
        this.platformSelection = platform;
    };

    private bufferizeTrackingEvent = (trackingEvent: ITrackingEvent): void => {
        this.trackingEventsBuffer.push(trackingEvent);
    };

    private flushTrackingEventsBuffer = (): void => {
        this.trackingEventsBuffer.map(trackingEvent => this.track(trackingEvent.eventName, trackingEvent.data));
        this.trackingEventsBuffer = [];
    };

    public updateAnalyticalData = (data: IAnalyticsData): void => {
        if (window[ANALYTICS_GLOBAL_NS]) {
            window[ANALYTICS_GLOBAL_NS] = {
                ...window[ANALYTICS_GLOBAL_NS],
                ...data
            };
        }
    };

    private track(eventName: ANALYTICS_EVENTS, data: ITrackingData): void {
        if (!this.hasUserAllowedTracking) {
            return this.bufferizeTrackingEvent({ eventName, data });
        }

        try {
            if (window[ANALYTICS_SDK] && isFunction(window[ANALYTICS_SDK].track)) {
                window[ANALYTICS_SDK].track(eventName, data);
            }
        } catch (err) {
            console.error('Analytics script failed to handle track method call', err); // eslint-disable-line no-console
        }
    }

    public initPrivacyOptions(): void {
        try {
            if (window[ANALYTICS_SDK] && isFunction(window[ANALYTICS_SDK].initPrivacyOptions)) {
                window[ANALYTICS_SDK].initPrivacyOptions();
            }
        } catch (err) {
            console.error('Analytics script failed to initialize privacy center', err); // eslint-disable-line no-console
        }
    }

    private assignInitialData = (configData: IAnalyticsConfigData): void => {
        const country = getCountryFromLanguage( { useUrl: true });
        const language = getShortLanguageCode({ useUrl: true });
        const region = COUNTRIES.find(c => c.code === country)?.region || '';

        window[ANALYTICS_GLOBAL_NS] = {
            siteCategory: SITE_CATEGORY,
            appId: configData.applicationId,
            brand: configData.analyticsBrand || '',
            country,
            countryCode: country.toLowerCase(),
            environment: this.analyticsEnvironment,
            game: configData.analyticsGame || '',
            genomeId: configData.genomeId || '',
            language,
            region,
            siteType: WEBSITE_DEFAULT_ANALYTIC_NAME,
            siteName: configData.analyticsSiteName || '',
            mdmBrand: configData.analyticsMDMBrandId || '',
            mdmInstallmentId: configData.analyticsMDMInstallmentId || '',
            errorType: '',
            pageName: '',
            siteSubSection: '',
            siteSection: '',
            platformSelection: '',
            ubisoftId: '',
            flowType: configData.flowType,
            flowStep: '',
            doNotTrack: 'NOT_ACTIVE',
        } as IAnalyticsData;
    };

    private injectAnalyticsScript = (envName: string): void => {
        const script: HTMLScriptElement = document.createElement('script');
        script.setAttribute('type', 'text/javascript');
        script.setAttribute('async', 'true');

        script.onload = () => {
            try {
                if (window[ANALYTICS_SDK] && isFunction(window[ANALYTICS_SDK].init)) {
                    window[ANALYTICS_SDK].init(window[ANALYTICS_GLOBAL_NS]);
                }
            } catch (err) {
                console.error('Analytics script failed to handle init method call', err);  // eslint-disable-line no-console
            }
        };

        script.setAttribute('src', getEnvDependentEndpoint({ envName, source: ANALYTICS_SCRIPT_URL }));

        document.body.appendChild(script);
    };

    public triggerOptIn = () => {
        try {
            if (window[ANALYTICS_SDK] && isFunction(window[ANALYTICS_SDK].triggerOptIn)) {
                window[ANALYTICS_SDK].triggerOptIn();
            }
        } catch (err) {
            console.error('Analytics script failed to handle triggerOptIn method call', err); // eslint-disable-line no-console
        }
    };

    public triggerInitialPageEvents({ userId = '', hasLoginSession }: {
        hasLoginSession: boolean,
        userId: string
    }) {
        const analyticsLoginAction = ANALYTICS_ATTRIBUTES.action;

        this.sendPageEvent({ pageName: 'main', siteSubSection: '', errorType: '' } as IAnalyticsData);
        userId && this.updateAnalyticalData({ ubisoftId: userId, errorType: '' });
        this.sendAccountEvent({
            action: hasLoginSession
                ? analyticsLoginAction.signedIn
                : analyticsLoginAction.signedOut,
            loggedIn: hasLoginSession.toString(),
            loggedOut: (!hasLoginSession).toString()
        });

        this.isAuthenticatedUser = hasLoginSession;
    }
}

const analyticsService = new Analytics();

export default analyticsService;
